import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyAccommodationApi } from '../../api/journeyAccommodationApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { Button, ConfirmDialog, EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  fillWorkspaceProductChrome,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  applyAccommodationLocationSelection,
  buildAccommodationWritePayload,
  classifyLogisticsError,
  draftFromAccommodationDetail,
  emptyAccommodationDraft,
  normalizeAccommodationDetail,
  normalizeAccommodationSummary,
  validateAccommodationDraft,
} from './accommodationModel';
import ActivityLocationPicker from '../journey-planning/ActivityLocationPicker';
import {
  createExternalLocationSearchProvider,
  readGooglePlacesBrowserKey,
  resolveExternalSearchModeFromEnv,
} from '../journey-planning/externalLocationProvider';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';

const WRITE_ROLES = new Set(['OWNER', 'COORDINATOR', 'MEMBER']);
const REMOVE_ROLES = new Set(['OWNER', 'COORDINATOR']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);

const staySearchProvider = createExternalLocationSearchProvider({
  mode: resolveExternalSearchModeFromEnv(),
  browserApiKey: readGooglePlacesBrowserKey(),
});

function beginCreateMode(setMode, setEditingId, setDraft, setFormError) {
  setMode('create');
  setEditingId(null);
  setDraft(emptyAccommodationDraft());
  setFormError('');
}

export default function JourneyStaysPage() {
  const { journeyId } = useParams();
  const { t } = useTravelTranslation();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [mode, setMode] = useState('list');
  const [draft, setDraft] = useState(emptyAccommodationDraft());
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    setFormError('');
    setConfirmDelete(null);
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyAccommodationApi.list(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      const rows = Array.isArray(listPayload) ? listPayload : [];
      setItems(rows.map(normalizeAccommodationSummary));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyLogisticsError(cause);
      if (kind === 'not-found' || classifyJourneyAccessError(cause) === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || classifyJourneyAccessError(cause) === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const canWrite = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && WRITE_ROLES.has(journey?.requesterRole);
  const canRemove = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && REMOVE_ROLES.has(journey?.requesterRole);

  async function runMutation(work) {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    try {
      await work();
      setMode('list');
      setEditingId(null);
      setDraft(emptyAccommodationDraft());
      setConfirmDelete(null);
      await load();
    } catch (cause) {
      const kind = classifyLogisticsError(cause);
      if (kind === 'validation') setFormError(WORKSPACE_PRODUCT_CHROME.formCheckFallback);
      else if (kind === 'forbidden') setFormError(WORKSPACE_PRODUCT_CHROME.permissionDeniedChange);
      else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || WORKSPACE_PRODUCT_CHROME.staysChangeFailed,
        );
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  async function submitForm(event) {
    event.preventDefault();
    const validation = validateAccommodationDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    const body = buildAccommodationWritePayload(draft);
    await runMutation(async () => {
      if (mode === 'edit' && editingId) {
        await journeyAccommodationApi.update(journey.journeyId, editingId, body);
      } else {
        await journeyAccommodationApi.create(journey.journeyId, body);
      }
    });
  }

  async function beginEdit(item) {
    setFormError('');
    setMode('edit');
    setEditingId(item.accommodationId);
    setDraft(emptyAccommodationDraft());
    try {
      const detail = normalizeAccommodationDetail(
        await journeyAccommodationApi.get(journey.journeyId, item.accommodationId),
      );
      setDraft(draftFromAccommodationDetail(detail));
    } catch (cause) {
      setFormError(
        resolveSafeUserVisibleError(cause, t, 'travel.common.loadFailed')
          || WORKSPACE_PRODUCT_CHROME.staysLoadDetailFailed,
      );
    }
  }

  async function confirmRemove(item) {
    await runMutation(async () => {
      await journeyAccommodationApi.remove(journey.journeyId, item.accommodationId, { confirm: true });
    });
  }

  function openCreate() {
    beginCreateMode(setMode, setEditingId, setDraft, setFormError);
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state" aria-live="polite">
        <LoadingState label={t('travel.common.loading')} data-testid="stays-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state" data-testid="stays-not-found">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.notFoundTitle}
          description={WORKSPACE_PRODUCT_CHROME.notFoundBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state" data-testid="stays-forbidden">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.forbiddenTitle}
          description={WORKSPACE_PRODUCT_CHROME.forbiddenBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;
    return (
      <main className="journey-workspace-state" data-testid="stays-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const showToolbarAdd = canWrite && (items.length > 0 || mode !== 'list');

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.destination}</dt>
                <dd>{journey.destination}</dd>
              </div>
            ) : null}
            {journey.dateRange ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.dates}</dt>
                <dd>{journey.dateRange}</dd>
              </div>
            ) : null}
            <div>
              <dt>{WORKSPACE_PRODUCT_CHROME.status}</dt>
              <dd>{journey.lifecycleLabel}</dd>
            </div>
            {journey.requesterRoleLabel ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.yourRole}</dt>
                <dd>{journey.requesterRoleLabel}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel" aria-labelledby="stays-heading">
        <div className="journey-workspace__panel-head">
          <div>
            <h2 id="stays-heading">{WORKSPACE_PRODUCT_CHROME.stays}</h2>
            <p className="journey-logistics__panel-lead">{WORKSPACE_PRODUCT_CHROME.staysPanelLead}</p>
          </div>
          <div className="journey-logistics__toolbar">
            <Button type="button" variant="secondary" onClick={load} disabled={busy}>
              {WORKSPACE_PRODUCT_CHROME.refresh}
            </Button>
            {showToolbarAdd ? (
              <Button type="button" variant="primary" disabled={busy} onClick={openCreate}>
                {WORKSPACE_PRODUCT_CHROME.addStay}
              </Button>
            ) : null}
          </div>
        </div>

        {formError ? <p className="journey-workspace__error" role="alert">{formError}</p> : null}

        {mode === 'create' || mode === 'edit' ? (
          <AccommodationForm
            draft={draft}
            setDraft={setDraft}
            onSubmit={submitForm}
            onCancel={() => { setMode('list'); setEditingId(null); setFormError(''); }}
            busy={busy}
            submitLabel={mode === 'edit' ? WORKSPACE_PRODUCT_CHROME.saveStay : WORKSPACE_PRODUCT_CHROME.createStay}
          />
        ) : null}

        {items.length === 0 && mode === 'list' ? (
          <EmptyState
            className="journey-logistics__empty"
            data-testid="stays-empty"
            title={WORKSPACE_PRODUCT_CHROME.staysEmptyTitle}
            description={WORKSPACE_PRODUCT_CHROME.staysEmptyDescription}
            action={
              canWrite ? (
                <Button type="button" variant="secondary" disabled={busy} onClick={openCreate}>
                  {WORKSPACE_PRODUCT_CHROME.addStay}
                </Button>
              ) : null
            }
          />
        ) : items.length === 0 ? null : (
          <ul className="journey-logistics__list">
            {items.map((item) => {
              const locationText = item.locationLabel
                || item.location?.name
                || WORKSPACE_PRODUCT_CHROME.staysLocationUnset;
              const hasDates = Boolean(item.checkInAt || item.checkOutAt);
              return (
                <li key={item.accommodationId} className="journey-logistics__item">
                  <div className="journey-logistics__item-main">
                    <div className="journey-logistics__item-title-row">
                      <strong>{item.propertyName}</strong>
                    </div>
                    <dl className="journey-logistics__meta">
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.staysLocationLabel}</dt>
                        <dd>{locationText}</dd>
                      </div>
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.staysCheckIn}</dt>
                        <dd>
                          {item.checkInAt
                            ? formatDateTime(item.checkInAt)
                            : WORKSPACE_PRODUCT_CHROME.staysDatesNotSet}
                        </dd>
                      </div>
                      <div className="journey-logistics__meta-row">
                        <dt>{WORKSPACE_PRODUCT_CHROME.staysCheckOut}</dt>
                        <dd>
                          {item.checkOutAt
                            ? formatDateTime(item.checkOutAt)
                            : (hasDates ? '—' : WORKSPACE_PRODUCT_CHROME.staysDatesNotSet)}
                        </dd>
                      </div>
                      {item.confirmationReference ? (
                        <div className="journey-logistics__meta-row">
                          <dt>{WORKSPACE_PRODUCT_CHROME.referencePrefix}</dt>
                          <dd>{item.confirmationReference}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                  <div className="journey-logistics__item-actions">
                    {canWrite ? (
                      <Button type="button" variant="secondary" disabled={busy} onClick={() => beginEdit(item)}>
                        {WORKSPACE_PRODUCT_CHROME.edit}
                      </Button>
                    ) : null}
                    {canRemove ? (
                      <Button type="button" variant="danger" disabled={busy} onClick={() => setConfirmDelete(item)}>
                        {WORKSPACE_PRODUCT_CHROME.delete}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={WORKSPACE_PRODUCT_CHROME.staysDeleteTitle}
        description={
          confirmDelete
            ? fillWorkspaceProductChrome(WORKSPACE_PRODUCT_CHROME.staysDeleteBody, {
              name: confirmDelete.propertyName,
            })
            : ''
        }
        confirmLabel={busy ? WORKSPACE_PRODUCT_CHROME.deleting : WORKSPACE_PRODUCT_CHROME.delete}
        cancelLabel={WORKSPACE_PRODUCT_CHROME.cancel}
        danger
        loading={busy}
        onConfirm={() => confirmDelete && confirmRemove(confirmDelete)}
        onCancel={() => { if (!busy) setConfirmDelete(null); }}
      />
    </main>
  );
}

function AccommodationForm({ draft, setDraft, onSubmit, onCancel, busy, submitLabel }) {
  const nameId = useId();
  const locationFieldId = useId();
  const addressId = useId();
  const checkInId = useId();
  const checkOutId = useId();
  const refId = useId();
  const noteId = useId();
  return (
    <form className="journey-logistics__form" onSubmit={onSubmit} noValidate>
      <label htmlFor={nameId}>
        {WORKSPACE_PRODUCT_CHROME.staysPropertyName}
        <input
          id={nameId}
          value={draft.propertyName}
          maxLength={200}
          required
          onChange={(e) => setDraft((c) => ({ ...c, propertyName: e.target.value }))}
        />
      </label>
      <div className="journey-workspace__dates">
        <label htmlFor={checkInId}>
          {WORKSPACE_PRODUCT_CHROME.staysCheckIn}
          <input
            id={checkInId}
            type="datetime-local"
            value={draft.checkInAt}
            onChange={(e) => setDraft((c) => ({ ...c, checkInAt: e.target.value }))}
          />
        </label>
        <label htmlFor={checkOutId}>
          {WORKSPACE_PRODUCT_CHROME.staysCheckOut}
          <input
            id={checkOutId}
            type="datetime-local"
            value={draft.checkOutAt}
            onChange={(e) => setDraft((c) => ({ ...c, checkOutAt: e.target.value }))}
          />
        </label>
      </div>
      <div className="journey-logistics__location-picker" data-testid="stay-location-picker">
        <span className="journey-logistics__field-label">{WORKSPACE_PRODUCT_CHROME.staysMapLocation}</span>
        <ActivityLocationPicker
          selected={draft.locationNode}
          onSelect={(node) => setDraft((current) => applyAccommodationLocationSelection(current, node))}
          searchProvider={staySearchProvider}
          disabled={busy}
        />
      </div>
      <label htmlFor={locationFieldId}>
        {WORKSPACE_PRODUCT_CHROME.staysLocationLabel}
        <input
          id={locationFieldId}
          value={draft.location}
          onChange={(e) => setDraft((c) => ({ ...c, location: e.target.value }))}
          placeholder={WORKSPACE_PRODUCT_CHROME.optionalSummaryPlaceholder}
        />
      </label>
      <label htmlFor={addressId}>
        {WORKSPACE_PRODUCT_CHROME.staysAddress}
        <input
          id={addressId}
          value={draft.addressText}
          maxLength={512}
          onChange={(e) => setDraft((c) => ({ ...c, addressText: e.target.value }))}
        />
      </label>
      <label htmlFor={refId}>
        {WORKSPACE_PRODUCT_CHROME.staysBookingReference}
        <input
          id={refId}
          value={draft.confirmationReference}
          maxLength={128}
          onChange={(e) => setDraft((c) => ({ ...c, confirmationReference: e.target.value }))}
        />
      </label>
      <label htmlFor={noteId}>
        {WORKSPACE_PRODUCT_CHROME.staysNote}
        <textarea
          id={noteId}
          rows={3}
          value={draft.note}
          onChange={(e) => setDraft((c) => ({ ...c, note: e.target.value }))}
        />
      </label>
      <div className="journey-workspace__edit-actions">
        <Button type="submit" variant="primary" loading={busy}>
          {busy ? WORKSPACE_PRODUCT_CHROME.saving : submitLabel}
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>
          {WORKSPACE_PRODUCT_CHROME.cancel}
        </Button>
      </div>
    </form>
  );
}
